import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, Paper, Stack, Chip,
  Button, IconButton, Tooltip, LinearProgress, Alert, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Select, MenuItem, FormControl, InputLabel, InputAdornment,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  CircularProgress, Snackbar, Collapse, Tabs, Tab, Slider
} from '@mui/material';
import {
  AccountBalance, TrendingUp, TrendingDown, Warning, CheckCircle,
  Add, Edit, Delete, Save, Refresh, Settings, Close,
  AttachMoney, Receipt, Person, BarChart as BarChartIcon,
  ArrowUpward, ArrowDownward
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, Legend, Cell, PieChart, Pie
} from 'recharts';
import { budgetService } from '../services/api';

// ─── Constantes ───────────────────────────────────────────────────────────────

const CURRENCIES = [
  { code: 'XAF', symbol: 'FCFA', label: 'Franc CFA (XAF)' },
  { code: 'EUR', symbol: '€',    label: 'Euro (EUR)' },
  { code: 'USD', symbol: '$',    label: 'Dollar US (USD)' },
  { code: 'GBP', symbol: '£',    label: 'Livre Sterling (GBP)' },
  { code: 'MAD', symbol: 'DH',   label: 'Dirham Marocain (MAD)' },
  { code: 'XOF', symbol: 'FCFA', label: 'Franc CFA UEMOA (XOF)' },
];

const EXPENSE_CATEGORIES = [
  { value: 'labor',    label: 'Main d\'œuvre',  color: '#3b82f6' },
  { value: 'material', label: 'Matériel',        color: '#f59e0b' },
  { value: 'service',  label: 'Prestation',      color: '#8b5cf6' },
  { value: 'travel',   label: 'Déplacement',     color: '#10b981' },
  { value: 'software', label: 'Logiciel',         color: '#6366f1' },
  { value: 'other',    label: 'Autre',            color: '#6b7280' },
];

const CAT_MAP = Object.fromEntries(EXPENSE_CATEGORIES.map(c => [c.value, c]));

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(amount, currency = 'XAF') {
  const sym = CURRENCIES.find(c => c.code === currency)?.symbol || currency;
  if (currency === 'XAF' || currency === 'XOF') {
    return `${Math.round(amount).toLocaleString('fr-FR')} ${sym}`;
  }
  return `${sym} ${amount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ─── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color, trend }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 2, borderColor: color + '40', height: '100%' }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: color + '18', display: 'flex' }}>
            {React.cloneElement(icon, { sx: { fontSize: 22, color } })}
          </Box>
          {trend !== undefined && (
            trend >= 0
              ? <ArrowUpward sx={{ fontSize: 16, color: '#ef4444' }} />
              : <ArrowDownward sx={{ fontSize: 16, color: '#10b981' }} />
          )}
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ mt: 1.5, mb: 0.25, color }}>
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        {sub && (
          <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Jauge circulaire ─────────────────────────────────────────────────────────

function BudgetGauge({ pct, currency, actual, estimated, alertThreshold }) {
  const capped   = Math.min(pct ?? 0, 100);
  const color    = pct >= 100 ? '#ef4444' : pct >= alertThreshold ? '#f59e0b' : '#10b981';
  const size     = 160;
  const stroke   = 14;
  const r        = (size - stroke) / 2;
  const circ     = 2 * Math.PI * r;
  const dash     = (capped / 100) * circ;

  return (
    <Box sx={{ position: 'relative', width: size, height: size, mx: 'auto' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={r} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circ - dash}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray .6s ease' }}
        />
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0, display: 'flex',
        flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }}>
        <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>
          {pct !== null ? `${pct}%` : '—'}
        </Typography>
        <Typography variant="caption" color="text.secondary">utilisé</Typography>
      </Box>
    </Box>
  );
}

// ─── Dialog dépense ───────────────────────────────────────────────────────────

function ExpenseDialog({ open, onClose, onSave, expense, currency }) {
  const empty = { title: '', amount: '', category: 'other', description: '', expense_date: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(expense
      ? { title: expense.title, amount: expense.amount, category: expense.category,
          description: expense.description || '', expense_date: expense.expense_date?.slice(0,10) || '' }
      : empty
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expense, open]);

  const sym = CURRENCIES.find(c => c.code === currency)?.symbol || currency;

  async function handleSave() {
    if (!form.title.trim() || !form.amount) return;
    setSaving(true);
    await onSave(form);
    setSaving(false);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {expense ? 'Modifier la dépense' : 'Ajouter une dépense'}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Libellé" fullWidth required
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
          />
          <TextField
            label="Montant" fullWidth required type="number"
            value={form.amount}
            onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
            InputProps={{ startAdornment: <InputAdornment position="start">{sym}</InputAdornment> }}
          />
          <FormControl fullWidth>
            <InputLabel>Catégorie</InputLabel>
            <Select
              value={form.category} label="Catégorie"
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
            >
              {EXPENSE_CATEGORIES.map(c => (
                <MenuItem key={c.value} value={c.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c.color }} />
                    {c.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Date de dépense" fullWidth type="date"
            value={form.expense_date}
            onChange={e => setForm(f => ({ ...f, expense_date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Description (optionnel)" fullWidth multiline rows={2}
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained" onClick={handleSave}
          disabled={saving || !form.title.trim() || !form.amount}
        >
          {saving ? <CircularProgress size={18} /> : (expense ? 'Enregistrer' : 'Ajouter')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Dialog paramètres budget ─────────────────────────────────────────────────

function SettingsDialog({ open, onClose, onSave, settings }) {
  const [form, setForm] = useState({ estimated_budget: 0, currency: 'XAF', alert_threshold: 80 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) setForm({ ...settings });
  }, [settings, open]);

  async function handleSave() {
    setSaving(true);
    await onSave(form);
    setSaving(false);
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Paramètres du budget</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Devise</InputLabel>
            <Select value={form.currency} label="Devise"
              onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>
              {CURRENCIES.map(c => (
                <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Budget estimé total" fullWidth type="number"
            value={form.estimated_budget}
            onChange={e => setForm(f => ({ ...f, estimated_budget: parseFloat(e.target.value) || 0 }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {CURRENCIES.find(c => c.code === form.currency)?.symbol}
                </InputAdornment>
              )
            }}
            helperText="Entrez 0 pour un budget non défini"
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Seuil d'alerte : <strong>{form.alert_threshold}%</strong>
            </Typography>
            <Slider
              value={form.alert_threshold}
              onChange={(_, v) => setForm(f => ({ ...f, alert_threshold: v }))}
              min={10} max={100} step={5}
              marks={[
                { value: 50, label: '50%' },
                { value: 80, label: '80%' },
                { value: 100, label: '100%' },
              ]}
              valueLabelDisplay="auto"
              sx={{ color: form.alert_threshold >= 90 ? '#ef4444' : form.alert_threshold >= 70 ? '#f59e0b' : '#10b981' }}
            />
            <Typography variant="caption" color="text.secondary">
              Une alerte s'affiche quand le budget atteint ce pourcentage.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={18} /> : 'Enregistrer'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────

export default function BudgetView({ projectId }) {
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [tab,         setTab]         = useState(0);
  const [expDialog,   setExpDialog]   = useState(false);
  const [editExp,     setEditExp]     = useState(null);
  const [settDialog,  setSettDialog]  = useState(false);
  const [editRates,   setEditRates]   = useState(false);
  const [ratesDraft,  setRatesDraft]  = useState([]);
  const [savingRates, setSavingRates] = useState(false);
  const [delConfirm,  setDelConfirm]  = useState(null);
  const [error,       setError]       = useState('');
  const [snack,       setSnack]       = useState({ open: false, msg: '', sev: 'success' });

  // ─── Chargement ──────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await budgetService.getBudget(projectId);
      setData(res.data);
    } catch (e) {
      setError('Impossible de charger les données budgétaires.');
      console.error(e);
    }
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  // ─── Helpers snack ────────────────────────────────────────────────────────

  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // ─── Paramètres ───────────────────────────────────────────────────────────

  async function handleSaveSettings(form) {
    try {
      const res = await budgetService.updateSettings(projectId, form);
      setData(res.data);
      setSettDialog(false);
      toast('Paramètres enregistrés');
    } catch (e) { toast(e.response?.data?.error || 'Erreur', 'error'); }
  }

  // ─── Taux horaires ────────────────────────────────────────────────────────

  function startEditRates() {
    setRatesDraft(data.member_costs.map(m => ({ ...m, draft_rate: m.hourly_rate })));
    setEditRates(true);
  }

  async function saveRates() {
    setSavingRates(true);
    try {
      const res = await budgetService.updateRates(projectId,
        ratesDraft.map(m => ({ user_id: m.user_id, hourly_rate: m.draft_rate || 0 }))
      );
      setData(res.data);
      setEditRates(false);
      toast('Taux horaires mis à jour');
    } catch (e) { toast(e.response?.data?.error || 'Erreur', 'error'); }
    setSavingRates(false);
  }

  // ─── Dépenses ─────────────────────────────────────────────────────────────

  async function handleSaveExpense(form) {
    try {
      let res;
      if (editExp) {
        res = await budgetService.updateExpense(editExp.id, form);
        toast('Dépense modifiée');
      } else {
        res = await budgetService.createExpense(projectId, form);
        toast('Dépense ajoutée');
      }
      setData(res.data);
      setExpDialog(false);
      setEditExp(null);
    } catch (e) { toast(e.response?.data?.error || 'Erreur', 'error'); }
  }

  async function handleDeleteExpense(id) {
    try {
      const res = await budgetService.deleteExpense(id);
      setData(res.data);
      setDelConfirm(null);
      toast('Dépense supprimée');
    } catch (e) { toast(e.response?.data?.error || 'Erreur', 'error'); }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
      <CircularProgress />
    </Box>
  );

  if (!data) return (
    <Alert severity="error">{error || 'Impossible de charger le budget.'}</Alert>
  );

  if (!data.settings || !data.summary) return (
    <Alert severity="error">
      Réponse API inattendue — structure : {JSON.stringify(Object.keys(data))}
    </Alert>
  );

  const { settings, summary, member_costs, expenses } = data;
  const cur  = settings.currency;
  const hasBudget = summary.estimated_budget > 0;

  // Données graphique estimé vs réel
  const chartData = [
    {
      name: 'Main d\'œuvre',
      Estimé: Math.round(summary.estimated_labor),
      Réel:   Math.round(summary.actual_labor),
    },
    {
      name: 'Dépenses',
      Estimé: Math.round(summary.expenses_total),
      Réel:   Math.round(summary.expenses_total),
    },
    {
      name: 'Total',
      Estimé: Math.round(summary.total_estimated_cost),
      Réel:   Math.round(summary.total_actual),
    },
  ];

  // Pie chart par catégorie
  const pieData = EXPENSE_CATEGORIES
    .filter(c => summary.by_category?.[c.value] > 0)
    .map(c => ({ name: c.label, value: Math.round(summary.by_category[c.value]), color: c.color }));
  if (summary.actual_labor > 0)
    pieData.unshift({ name: 'Main d\'œuvre', value: Math.round(summary.actual_labor), color: '#3b82f6' });

  return (
    <Box>
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalance color="primary" />
            Gestion du Budget
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Suivi budgétaire : estimé vs réel, coûts main d'œuvre et dépenses.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Tooltip title="Actualiser">
            <IconButton onClick={load}><Refresh /></IconButton>
          </Tooltip>
          <Button variant="outlined" startIcon={<Settings />} onClick={() => setSettDialog(true)}>
            Paramètres
          </Button>
          <Button variant="contained" startIcon={<Add />}
            onClick={() => { setEditExp(null); setExpDialog(true); }}>
            Dépense
          </Button>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Alerte dépassement ──────────────────────────────────────────── */}
      <Collapse in={summary.alert_triggered && hasBudget}>
        <Alert
          severity={summary.usage_pct >= 100 ? 'error' : 'warning'}
          icon={<Warning />}
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setSettDialog(true)}>
              Ajuster
            </Button>
          }
        >
          <strong>
            {summary.usage_pct >= 100
              ? `⛔ Budget dépassé ! (${summary.usage_pct}% utilisé)`
              : `⚠️ Attention : ${summary.usage_pct}% du budget consommé`}
          </strong>
          {' '}— Seuil d'alerte : {settings.alert_threshold}%.
          {summary.remaining !== null && summary.remaining < 0 && (
            <> Dépassement : <strong>{fmt(Math.abs(summary.remaining), cur)}</strong>.</>
          )}
        </Alert>
      </Collapse>

      {/* ── Vue d'ensemble : jauge + KPIs ───────────────────────────────── */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Jauge */}
        <Grid item xs={12} sm={4} md={3}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
            <CardContent sx={{ textAlign: 'center', pt: 3 }}>
              <BudgetGauge
                pct={summary.usage_pct}
                currency={cur}
                actual={summary.total_actual}
                estimated={summary.estimated_budget}
                alertThreshold={settings.alert_threshold}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {hasBudget
                  ? <>Budget : <strong>{fmt(summary.estimated_budget, cur)}</strong></>
                  : <em>Budget non défini</em>
                }
              </Typography>
              {hasBudget && summary.remaining !== null && (
                <Typography variant="caption" sx={{ color: summary.remaining >= 0 ? 'success.main' : 'error.main' }}>
                  {summary.remaining >= 0
                    ? `Reste : ${fmt(summary.remaining, cur)}`
                    : `Dépassement : ${fmt(Math.abs(summary.remaining), cur)}`}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* KPIs */}
        <Grid item xs={12} sm={8} md={9}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                icon={<AccountBalance />}
                label="Coût total réel"
                value={fmt(summary.total_actual, cur)}
                sub={hasBudget ? `sur ${fmt(summary.estimated_budget, cur)}` : 'Aucun budget fixé'}
                color="#1976d2"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                icon={<Person />}
                label="Coût main d'œuvre"
                value={fmt(summary.actual_labor, cur)}
                sub={`Estimé : ${fmt(summary.estimated_labor, cur)}`}
                color="#8b5cf6"
                trend={summary.actual_labor - summary.estimated_labor}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                icon={<Receipt />}
                label="Dépenses diverses"
                value={fmt(summary.expenses_total, cur)}
                sub={`${expenses.length} entrée${expenses.length !== 1 ? 's' : ''}`}
                color="#f59e0b"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <KpiCard
                icon={hasBudget && summary.remaining >= 0 ? <CheckCircle /> : <TrendingUp />}
                label={hasBudget ? 'Restant' : 'Coût estimé'}
                value={hasBudget
                  ? fmt(Math.abs(summary.remaining ?? 0), cur)
                  : fmt(summary.total_estimated_cost, cur)}
                sub={hasBudget && summary.remaining < 0 ? '⛔ Dépassement' : undefined}
                color={hasBudget && summary.remaining < 0 ? '#ef4444' : '#10b981'}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* ── Onglets ──────────────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Graphiques" icon={<BarChartIcon />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label={`Coûts membres (${member_costs.length})`} icon={<Person />} iconPosition="start" sx={{ minHeight: 48 }} />
          <Tab label={`Dépenses (${expenses.length})`} icon={<Receipt />} iconPosition="start" sx={{ minHeight: 48 }} />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>

          {/* ── Tab 0 : Graphiques ──────────────────────────────────────── */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={3}>
              {/* Bar chart estimé vs réel */}
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  Estimé vs Réel
                </Typography>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={chartData} barGap={4}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                    <YAxis tickFormatter={v => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} tick={{ fontSize: 11 }} />
                    <RechartsTooltip formatter={(v) => fmt(v, cur)} />
                    <Legend />
                    <Bar dataKey="Estimé" fill="#bfdbfe" radius={[4,4,0,0]} />
                    <Bar dataKey="Réel"   fill="#1976d2" radius={[4,4,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>

              {/* Pie chart répartition */}
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                  Répartition des coûts réels
                </Typography>
                {pieData.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
                    <Typography color="text.secondary" variant="body2">Aucun coût enregistré</Typography>
                  </Box>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <PieChart>
                      <Pie data={pieData} dataKey="value" nameKey="name"
                        cx="50%" cy="50%" outerRadius={90} paddingAngle={3}
                        label={({ name, percent }) => `${name} ${Math.round(percent*100)}%`}
                        labelLine={false}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip formatter={(v) => fmt(v, cur)} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </Grid>

              {/* Barre de progression budget */}
              {hasBudget && (
                <Grid item xs={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                    Consommation du budget ({summary.usage_pct}%)
                  </Typography>
                  <Box sx={{ position: 'relative' }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(summary.usage_pct ?? 0, 100)}
                      color={summary.usage_pct >= 100 ? 'error' : summary.usage_pct >= settings.alert_threshold ? 'warning' : 'success'}
                      sx={{ height: 20, borderRadius: 2 }}
                    />
                    {/* Marqueur seuil alerte */}
                    <Box sx={{
                      position: 'absolute',
                      left: `${settings.alert_threshold}%`,
                      top: 0, bottom: 0,
                      width: 2,
                      bgcolor: '#f59e0b',
                      opacity: 0.8,
                    }} />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(summary.total_actual, cur)} dépensés
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ color: '#f59e0b' }}>
                      ⚠ Seuil {settings.alert_threshold}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmt(summary.estimated_budget, cur)} budget total
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>

          {/* ── Tab 1 : Coûts membres ────────────────────────────────────── */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Coût basé sur les heures loguées × taux horaire configuré.
              </Typography>
              {!editRates ? (
                <Button size="small" startIcon={<Edit />} variant="outlined" onClick={startEditRates}>
                  Modifier les taux
                </Button>
              ) : (
                <Stack direction="row" spacing={1}>
                  <Button size="small" onClick={() => setEditRates(false)}>Annuler</Button>
                  <Button size="small" variant="contained" startIcon={<Save />}
                    onClick={saveRates} disabled={savingRates}>
                    {savingRates ? <CircularProgress size={16} /> : 'Enregistrer'}
                  </Button>
                </Stack>
              )}
            </Box>

            {member_costs.length === 0 ? (
              <Alert severity="info">Aucun membre avec des tâches assignées.</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                      <TableCell>Membre</TableCell>
                      <TableCell align="right">Taux horaire</TableCell>
                      <TableCell align="right">H. loguées</TableCell>
                      <TableCell align="right">H. estimées</TableCell>
                      <TableCell align="right">Coût réel</TableCell>
                      <TableCell align="right">Coût estimé</TableCell>
                      <TableCell align="right">Écart</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(editRates ? ratesDraft : member_costs).map((m, i) => {
                      const ecart = (editRates ? m.actual_cost : m.actual_cost) - m.estimated_cost;
                      return (
                        <TableRow key={m.user_id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>{m.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{m.email}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            {editRates ? (
                              <TextField
                                size="small" type="number"
                                value={m.draft_rate}
                                onChange={e => {
                                  const v = parseFloat(e.target.value) || 0;
                                  setRatesDraft(d => d.map((r, ri) => ri === i ? { ...r, draft_rate: v } : r));
                                }}
                                InputProps={{ inputProps: { min: 0, style: { textAlign: 'right' } } }}
                                sx={{ width: 100 }}
                              />
                            ) : (
                              <Typography variant="body2">
                                {m.hourly_rate > 0 ? fmt(m.hourly_rate, cur) + '/h' : <em style={{ color: '#9ca3af' }}>Non défini</em>}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>{m.logged_hours}h</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">{m.estimated_hours}h</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700} color="primary">
                              {fmt(m.actual_cost, cur)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" color="text.secondary">
                              {fmt(m.estimated_cost, cur)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2"
                              sx={{ color: ecart > 0 ? '#ef4444' : ecart < 0 ? '#10b981' : 'text.secondary' }}
                            >
                              {ecart > 0 ? '+' : ''}{fmt(ecart, cur)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Ligne totaux */}
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                      <TableCell>Total</TableCell>
                      <TableCell />
                      <TableCell align="right">
                        {member_costs.reduce((s, m) => s + m.logged_hours, 0)}h
                      </TableCell>
                      <TableCell align="right">
                        {member_costs.reduce((s, m) => s + m.estimated_hours, 0)}h
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#1976d2' }}>
                        {fmt(summary.actual_labor, cur)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>
                        {fmt(summary.estimated_labor, cur)}
                      </TableCell>
                      <TableCell align="right"
                        sx={{ color: summary.actual_labor - summary.estimated_labor > 0 ? '#ef4444' : '#10b981' }}>
                        {summary.actual_labor - summary.estimated_labor > 0 ? '+' : ''}
                        {fmt(summary.actual_labor - summary.estimated_labor, cur)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* ── Tab 2 : Dépenses ────────────────────────────────────────── */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Total : <strong>{fmt(summary.expenses_total, cur)}</strong>
              </Typography>
              <Button size="small" variant="contained" startIcon={<Add />}
                onClick={() => { setEditExp(null); setExpDialog(true); }}>
                Ajouter une dépense
              </Button>
            </Box>

            {expenses.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 5, textAlign: 'center', borderStyle: 'dashed', borderRadius: 2 }}>
                <Receipt sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">Aucune dépense enregistrée.</Typography>
                <Button sx={{ mt: 2 }} size="small" startIcon={<Add />}
                  onClick={() => { setEditExp(null); setExpDialog(true); }}>
                  Ajouter la première dépense
                </Button>
              </Paper>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'action.hover' } }}>
                      <TableCell>Libellé</TableCell>
                      <TableCell>Catégorie</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Ajouté par</TableCell>
                      <TableCell align="right">Montant</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expenses.map(e => {
                      const cat = CAT_MAP[e.category] || CAT_MAP.other;
                      return (
                        <TableRow key={e.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{e.title}</Typography>
                            {e.description && (
                              <Typography variant="caption" color="text.secondary">{e.description}</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={cat.label} size="small"
                              sx={{ bgcolor: cat.color + '20', color: cat.color, fontWeight: 600, border: `1px solid ${cat.color}40` }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {e.expense_date ? new Date(e.expense_date).toLocaleDateString('fr-FR') : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {e.created_by_name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={700} color="primary">
                              {fmt(e.amount, cur)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Tooltip title="Modifier">
                                <IconButton size="small" onClick={() => { setEditExp(e); setExpDialog(true); }}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton size="small" color="error" onClick={() => setDelConfirm(e)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                      <TableCell colSpan={4}>Total dépenses</TableCell>
                      <TableCell align="right" sx={{ color: '#1976d2' }}>
                        {fmt(summary.expenses_total, cur)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}

      <ExpenseDialog
        open={expDialog}
        onClose={() => { setExpDialog(false); setEditExp(null); }}
        onSave={handleSaveExpense}
        expense={editExp}
        currency={cur}
      />

      <SettingsDialog
        open={settDialog}
        onClose={() => setSettDialog(false)}
        onSave={handleSaveSettings}
        settings={settings}
      />

      {/* Confirmation suppression dépense */}
      <Dialog open={Boolean(delConfirm)} onClose={() => setDelConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Supprimer cette dépense ?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            « {delConfirm?.title} » — <strong>{delConfirm ? fmt(delConfirm.amount, cur) : ''}</strong>
            <br />Cette action est irréversible.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDelConfirm(null)}>Annuler</Button>
          <Button color="error" variant="contained" onClick={() => handleDeleteExpense(delConfirm.id)}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.open} autoHideDuration={4000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
